import { useMutation } from '@tanstack/react-query';
import { Col, Row, TableColumnsType } from 'antd';
import {
  ICalculateRevenueHoTeamsale,
  IGetTrackRevenueHo,
} from 'apis/GraphQL/Variables/hoPlan';
import HOPlanApi from 'apis/HOPlanApis';
import { PlantApi } from 'apis/PlantApis';
import { IconLoading } from 'assets/svg';
import { Button, ButtonFilter } from 'components';
import ButtonExport from 'components/Button/ButtonExport';
import { UploadModal } from 'components/UploadModal_V2';
import { useTitle } from 'contexts/TitleProvider';
import { useUser } from 'contexts/UserProvider';
import dayjs from 'dayjs';
import useQueryParam from 'hooks/useQueryPrams';
import { IHOPlan } from 'interfaces/HOPlan/HOPlan';
import { IPlant } from 'interfaces/Plant/Plant';
import Content from 'layouts/Content';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import path from 'routes/Path';
import { DEFAULT_PAGE_SIZE } from 'utils/constants/constant';
import { exportExcel_v2 } from 'utils/constants/exportExcel';
import {
  generateUUID,
  toastError,
  toastSuccess,
} from 'utils/constants/functions';
import { icons } from 'utils/constants/icons';
import { IMPORT_PLANNING_HO } from 'utils/constants/importId';
import FilterHOFollowPlan from './FilterHOFollowPlan';
import TableHOFollowPlan from './TableHOFollowPlan';

const { EyeOutlined } = icons;

export type IHOTablePlan = {
  tplantName: string;
  tcreateUser: string;
  tmodifyUser: string;
} & Omit<
  IHOPlan,
  'approval' | 'tcreateUser' | 'tmodifyUser' | 'tplant' | 'trevenuePlanBranches'
>;

export interface IData {
  items: IHOTablePlan[];
  pageInfo: any;
}

export interface IFilterRevenuePlanBranch {
  year?: any;
  month?: any;
  plantId?: string;
  typeRevenue?: string;
  teamSalesId?: string;
}

const initData: IData = { items: [], pageInfo: {} };

const HOFollowPlan = () => {
  const { user } = useUser(),
    { setTitle, setBreadcrumb } = useTitle();

  const navigate = useNavigate();
  const tableRef = useRef<any>(null);

  const [isOpen, setIsOpen] = useState(false),
    [isOpenImportModal, setIsOpenImportModal] = useState(false);
  const [, setEditIndex] = useState<number>(-1);
  const [data, setData] = useState<IData>(initData),
    [plants, setPlants] = useState<IPlant[]>([]),
    [headerRow, setHeaderRow] = useState<TableColumnsType>([]),
    [filterData, setFilterData] = useState<IFilterRevenuePlanBranch>({
      year: dayjs(new Date().getFullYear().toString(), 'YYYY'),
      month: [dayjs().month(0), dayjs()],
    });

  const queryParam = useQueryParam(),
    page = parseInt(queryParam.get('page') + '') || 1,
    page_size = parseInt(queryParam.get('page_size') + '') || DEFAULT_PAGE_SIZE;

  const defaultFilter = useMemo(() => {
    if (user?.isCN || user?.isSale)
      return {
        plantId: (user as any)?.plantCode,
      };

    return {};
  }, [user]);

  const getHOPlantsMutation = useMutation({
      mutationFn: (params: IGetTrackRevenueHo) =>
        HOPlanApi.getTrackRevenueHo(params),
      onSuccess: (res, variables) => {
        const data = res.data?.getTrackRevenueHo || {};
        const items = (data?.items || []).map((item: any) => ({
          ...item,
          id: generateUUID(),
        }));

        if (typeof variables.export !== 'undefined') {
          exportExcel_v2(
            headerRow,
            data?.items || [],
            'Vận hành - [HO] Theo dõi KH',
          );
        } else
          setData({
            ...data,
            items,
          });
      },
      onError: (error) => {
        console.log(error);
      },
    }),
    { mutate: getAllPlant } = useMutation({
      mutationFn: (id?: string) => PlantApi.getAllPlant(id),
      onSuccess: (res) => {
        setPlants(res.data.getPlant.items);
      },
      onError: (error) => console.log(error),
    });

  const {
    mutate: calculateRevenueHoTeamsale,
    isPending: isPendingCalculateRevenueHoTeamsale,
  } = useMutation({
    mutationFn: (params: ICalculateRevenueHoTeamsale) =>
      HOPlanApi.calculateRevenueHoTeamsale(params),
    onSuccess: (res) => {
      if (res.data?.calculateRevenueHoTeamsale?.success) {
        toastSuccess('Cập nhật thành công');

        const params = getTrackRevenueHoParams({});
        if (params) getHOPlantsMutation.mutate(params);
      } else {
        toastError(
          res.data?.calculateRevenueHoTeamsale?.message || 'Có lỗi xảy ra',
        );
      }
    },
    onError: (error) => {
      console.log(error);
      toastError(error.message || 'Có lỗi xảy ra');
    },
  });

  useEffect(() => {
    setTitle('[HO] Theo dõi kế hoạch chi nhánh');
    setBreadcrumb([{ title: 'Vận hành' }, { title: '[HO] Theo dõi KH ' }]);
  }, [setBreadcrumb, setTitle]);

  useEffect(() => {
    let plantId = user?.plantCode;
    if (user?.isAdmin || user?.isHO) plantId = undefined;

    getAllPlant(plantId);
  }, [getAllPlant, user]);

  const getTrackRevenueHoParams = useCallback(
    ({
      headerRow,
      totalItems,
    }: {
      headerRow?: TableColumnsType;
      totalItems?: number;
    }): IGetTrackRevenueHo | undefined => {
      const { year, month, plantId, typeRevenue } = filterData;

      if (!year || !month) return undefined;

      const take = headerRow ? (totalItems ?? 1000) : page_size;
      const skip = headerRow ? 0 : (page - 1) * page_size;

      return {
        skip,
        take,
        year: dayjs(year).year(),
        fromMonth: dayjs(month[0]).month() + 1,
        toMonth: dayjs(month[1]).month() + 1,
        plantId,
        typeRevenue,
        ...(headerRow && { export: { headerRow } }),
      };
    },
    [filterData, page, page_size],
  );

  useEffect(() => {
    const params = getTrackRevenueHoParams({});
    if (params) getHOPlantsMutation.mutate(params);
  }, [getTrackRevenueHoParams]);

  useEffect(() => {
    if (!tableRef.current) return;
    const { headerRow } = tableRef.current.getData();
    headerRow.pop();

    setHeaderRow(headerRow);
  }, [tableRef]);

  const handleExportData = useCallback(() => {
    const params = getTrackRevenueHoParams({
      headerRow,
      totalItems: data.pageInfo?.totalItems,
    });
    if (params) getHOPlantsMutation.mutate(params);
  }, [data.pageInfo?.totalItems, getTrackRevenueHoParams, headerRow]);

  const handleOnCancelFilter = () => {
    navigate(`?page=1&page_size=${page_size}`);

    setFilterData({
      year: dayjs(new Date().getFullYear().toString(), 'YYYY'),
      month: [dayjs().month(0), dayjs()],
    });
  };

  const handleOnOkFilter = (values: any) => {
    navigate(`?page=1&page_size=${page_size}`);

    setFilterData(values);
  };

  const refetchPlan = useCallback(() => {
    const params = getTrackRevenueHoParams({});
    if (params) getHOPlantsMutation.mutate(params);
  }, [getTrackRevenueHoParams]);

  const handleCancelEdit = () => {
    setEditIndex(-1);
  };

  const handleRowChange = (record: any, value: any) => {
    console.log(record);
    console.log(value);
  };

  const handleCalculateRevenueHoTeamsale = () => {
    const plantSelected = filterData.plantId;

    const year = dayjs(filterData.year).year();
    const plantId =
      plantSelected === '5000' || !plantSelected
        ? ['5001', '5002', '5004', '5005']
        : [plantSelected];

    calculateRevenueHoTeamsale({
      year,
      plantId,
    });
  };

  return (
    <>
      <Row gutter={[8, 16]} align="middle" justify="end">
        <Col>
          <ButtonFilter isOpen={isOpen} setIsOpen={setIsOpen} />
        </Col>
        <Col>
          <ButtonExport onClick={handleExportData} />
        </Col>
        <Col>
          <Button
            onClick={() =>
              navigate(`${path.HO_FOLLOW_PLAN_DETAIL}`, {
                state: {
                  year:
                    dayjs(filterData.year).year() ?? new Date().getFullYear(),
                },
              })
            }
            title="Xem hội sở"
            iconBefore={<EyeOutlined className="text-xl" />}
          />
        </Col>
        <Col>
          <Button
            onClick={handleCalculateRevenueHoTeamsale}
            fill
            title="Cập nhật"
            iconBefore={<IconLoading />}
            loading={isPendingCalculateRevenueHoTeamsale}
          />
        </Col>
        {/* {(user?.isCN || user?.isCN) && (
          <Col>
            <Button
              title="Đề xuất thay đổi"
              iconBefore={<EditOutlined />}
              handleOnclick={handleOpenProposedModal}
            />
          </Col>
        )} */}
      </Row>
      {/* <ModalProposedChange
        open={isOpenProposedModal}
        title={'Đề xuất thay đổi'}
        classNames={{
          content: '!p-0',
          header:
            '!px-[24px] !pt-[20px] !pb-[8px] !border-sub !border-solid !border-b-[1px]',
          body: '!px-[24px] !pb-[20px]',
          footer: '!px-[24px] !py-[20px] !bg-rowTable !rounded-b-[8px]',
        }}
        className="!w-max max-w-[70%]"
        footer={() => (
          <Row gutter={[8, 16]} justify={'end'} align={'middle'}>
            <Col>
              <Button
                fill
                title="Hủy"
                customClass="!bg-rowTable !border-transparent !text-neutral-30"
                handleOnclick={handleCancelModalClick}
              />
            </Col>
            <Col>
              <Button
                fill
                title="Đề xuất hội sở"
                handleOnclick={handleProposedClick}
              />
            </Col>
          </Row>
        )}
        onCancel={handleCancelModalClick}
      >
        <Row gutter={[8, 16]} className="flex-col">
          <Col>
            <Radio.Group
              buttonStyle="solid"
              defaultValue={selectedRadioVal}
              onChange={handleRadioChange}
            >
              <Radio value={1}>Import file</Radio>
              <Radio value={2}>Điều chỉnh trên bảng</Radio>
            </Radio.Group>
          </Col>
          <Col>
            {selectedRadioVal === 1 && (
              <Upload
                fileName={fileName}
                setFileName={setFileName}
                setData={handleUploadFile}
              />
            )}
          </Col>
          <Col>
            <TableHOFollowPlan
              data={{ items: uploadData, pageInfo: {} }}
              pagination={false}
              setData={setUploadData}
              editIndex={editIndex}
              modules={selectedRadioVal === 1 ? [] : [IModules.EDIT]}
              setEditIndex={setEditIndex}
              onCancel={handleCancelEdit}
              onRowChange={handleRowChange}
            />
          </Col>
        </Row>
      </ModalProposedChange> */}
      <UploadModal
        importId={IMPORT_PLANNING_HO} // FIXME
        open={isOpenImportModal}
        title={'Thêm mới kế hoạch kinh doanh'}
        refetch={refetchPlan}
        setOpen={setIsOpenImportModal}
        templateFileName="Vận hành - [HO] Theo dõi KH - Template"
      />
      <FilterHOFollowPlan
        defaultFilter={defaultFilter}
        isOpen={isOpen}
        plantData={plants}
        onFilter={handleOnOkFilter}
        onCancel={handleOnCancelFilter}
      />
      <Content>
        <TableHOFollowPlan
          data={data}
          ref={tableRef}
          loading={
            getHOPlantsMutation.isPending || isPendingCalculateRevenueHoTeamsale
          }
          setEditIndex={setEditIndex}
          onCancel={handleCancelEdit}
          onRowChange={handleRowChange}
        />
      </Content>
    </>
  );
};

export default HOFollowPlan;
